import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/api/index'
import { storage } from '@/composables/useStorage'

export const useMenuStore = defineStore('menu', () => {
  const categories = ref([])
  const products = ref([])
  const productsByCategory = ref({})
  const stopList = ref([])
  const favorites = ref(new Set())
  const isLoading = ref(false)

  // Computed
  const availableProducts = computed(() => {
    return products.value.filter(product => {
      // Проверяем, не в стоп-листе ли товар
      return !stopList.value.includes(product.id)
    })
  })

  // Computed для группировки продуктов по категориям (fallback если нет из API)
  const groupedProductsByCategory = computed(() => {
    const grouped = {}
    availableProducts.value.forEach(product => {
      const categoryId = product.category_id || product.parentGroup || product.productCategoryId || 'other'
      if (!grouped[categoryId]) {
        grouped[categoryId] = []
      }
      grouped[categoryId].push(product)
    })
    return grouped
  })

  const favoriteProducts = computed(() => {
    return availableProducts.value.filter(product => 
      favorites.value.has(product.id)
    )
  })

  // Загрузка меню
  const loadMenu = async () => {
    isLoading.value = true
    try {
      const response = await apiClient.getMenu()
      
      if (response.success) {
        categories.value = response.categories || []
        // Новый API возвращает productsByCategory
        if (response.productsByCategory) {
          productsByCategory.value = response.productsByCategory
          // Также сохраняем все продукты в плоском массиве для совместимости
          const allProducts = []
          Object.values(response.productsByCategory).forEach(categoryProducts => {
            allProducts.push(...categoryProducts)
          })
          products.value = allProducts
        } else {
          productsByCategory.value = {}
          products.value = []
        }
        // Стоп-лист из ответа API
        stopList.value = response.stop_list || []
        return true
      }
      
      throw new Error(response.message || 'Ошибка загрузки меню')
    } catch (error) {
      console.error('[Menu] Load menu error:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // Загрузка категорий
  const loadCategories = async () => {
    try {
      const response = await apiClient.getCategories()
      
      if (response.success) {
        categories.value = response.categories || []
        return categories.value
      }
      
      throw new Error(response.message || 'Ошибка загрузки категорий')
    } catch (error) {
      console.error('[Menu] Load categories error:', error)
      throw error
    }
  }

  // Загрузка стоп-листа
  const loadStopList = async () => {
    try {
      const response = await apiClient.getStopList()
      
      if (response.success) {
        stopList.value = response.stop_list || []
        return stopList.value
      }
      
      throw new Error(response.message || 'Ошибка загрузки стоп-листа')
    } catch (error) {
      console.error('[Menu] Load stop list error:', error)
      throw error
    }
  }

  // Загрузка избранного
  const loadFavorites = async () => {
    // Если пользователь не авторизован, загружаем избранное только из локального хранилища
    const token = storage.get('auth_token')
    if (!token) {
      const savedFavorites = storage.get('favorites', [])
      favorites.value = new Set(savedFavorites)
      return savedFavorites
    }

    try {
      const response = await apiClient.getFavorites()
      if (response && response.success && response.favorites) {
        // Сохраняем ID товаров в Set для быстрой проверки
        const productIds = response.favorites.map(p => p.id)
        favorites.value = new Set(productIds)
        return response.favorites
      }
      favorites.value = new Set()
      return []
    } catch (error) {
      // Тихо обрабатываем ошибки авторизации
      const errorMsg = error?.message?.toLowerCase() || ''
      const isAuthError = errorMsg.includes('авторизац') || 
                          errorMsg.includes('authorization') ||
                          errorMsg.includes('header') ||
                          errorMsg.includes('token') ||
                          error?.status === 401 ||
                          error?.response?.status === 401
      
      if (!isAuthError) {
        console.error('[Menu] Load favorites error:', error)
      }
      
      // Fallback к localStorage если API недоступен
      const savedFavorites = storage.get('favorites', [])
      favorites.value = new Set(savedFavorites)
      return Array.from(favorites.value)
    }
  }

  // Добавить в избранное
  const addToFavorites = async (productId) => {
    favorites.value.add(productId)
    storage.set('favorites', Array.from(favorites.value))

    const token = storage.get('auth_token')
    if (!token) {
      return true
    }

    try {
      const response = await apiClient.addFavorite(productId)
      if (response && response.success) {
        return true
      }
      return true
    } catch (error) {
      // Не прерываем UI при ошибке бэкенда, локально товар уже добавлен
      return true
    }
  }

  // Удалить из избранного
  const removeFromFavorites = async (productId) => {
    favorites.value.delete(productId)
    storage.set('favorites', Array.from(favorites.value))

    const token = storage.get('auth_token')
    if (!token) {
      return true
    }

    try {
      const response = await apiClient.removeFavorite(productId)
      if (response && response.success) {
        return true
      }
      return true
    } catch (error) {
      // Не прерываем UI при ошибке бэкенда
      return true
    }
  }

  // Переключить избранное
  const toggleFavorite = async (productId) => {
    if (favorites.value.has(productId)) {
      return removeFromFavorites(productId)
    } else {
      return addToFavorites(productId)
    }
  }

  // Проверка, в избранном ли товар
  const isFavorite = (productId) => {
    return favorites.value.has(productId)
  }

  // Проверка, в стоп-листе ли товар
  const isInStopList = (productId) => {
    // Проверяем через stopList или через isAvailable в продукте
    if (stopList.value.includes(productId)) {
      return true
    }
    const product = products.value.find(p => p.id === productId)
    return product ? !product.isAvailable : false
  }

  // Поиск товаров
  const searchProducts = (query) => {
    if (!query || query.trim() === '') {
      return availableProducts.value
    }
    
    const lowerQuery = query.toLowerCase().trim()
    return availableProducts.value.filter(product => {
      const name = (product.name || '').toLowerCase()
      const description = (product.description || '').toLowerCase()
      return name.includes(lowerQuery) || description.includes(lowerQuery)
    })
  }

  // Получить товар по ID
  const getProduct = (productId) => {
    return products.value.find(p => p.id === productId)
  }

  // Получить категорию по ID
  const getCategory = (categoryId) => {
    return categories.value.find(c => c.id === categoryId)
  }

  return {
    // State
    categories,
    products,
    productsByCategory,
    stopList,
    favorites,
    isLoading,
    // Computed
    availableProducts,
    favoriteProducts,
    // Actions
    loadMenu,
    loadCategories,
    loadStopList,
    loadFavorites,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    isInStopList,
    searchProducts,
    getProduct,
    getCategory
  }
})

